import { faBalanceScaleLeft } from "@fortawesome/pro-solid-svg-icons";
import React from "react";
import { MapStateToPropsParam } from "react-redux";

import styles from "@/mk2/apps/strollers/containers/Brands/Brands.mscss";
import { StrollersComparison } from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison";
import {
    strollersComparisonLoad,
    strollersComparisonRemoveAll,
    strollersComparisonStoreOrdering,
    strollersComparisonToggle,
} from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.actions";
import reducer from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.reducers";
import saga from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.sagas";
import { getStrollersComparisonState } from "@/mk2/apps/strollers/containers/StrollersComparison/StrollersComparison.selectors";
import { strollersUrl } from "@/mk2/apps/strollers/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { MapDispatchToPropsObject } from "@/mk2/helpers/types";
import Page, { HalfpageProps, PageRouteProps } from "@/mk2/pages/Page";
import { AppState, getRequestUser } from "@/mk2/reducers";
import { LoadingState } from "@/mk2/reducers/loadingState";
import {
    PhotoDetailSchema,
    StrollerEntity,
    StrollerPhoto,
    StrollerSchema,
    StrollerVariantEntity,
    UserEntity,
} from "@/mk2/schemas";
import { getDenormalizedEntities } from "@/mk2/selectors";
import { HistoryLocationWithState } from "@/mk2/services/browserHistory";
import { Store } from "@/mk2/store/configureStore";
import {
    STROLLERS_COMPARISON,
    STROLLERS_COMPARISON_REMOVE_ALL,
    STROLLERS_COMPARISON_TITLE,
    STROLLER_SEO_GENERAL_DESC,
} from "@/mk/autogenerated/translation";
import { isAuthenticated } from "@/mk/bazaar/common/userUtils";

interface OwnProps {}

interface StateProps {
    loadingState: LoadingState;
    errorMessage: string;
    requestUser: UserEntity;
    strollers: StrollerEntity[];
    strollersPreviewPhotoMap: Record<number, number>;
    strollersPhotosMap: Record<number, StrollerPhoto>;
    count: number;
    comparison: number[];
}

interface DispatchProps {
    onLoad();
    onRemoveAll(location: HistoryLocationWithState);
    onStoreOrdering(orderedIds: number[], location: HistoryLocationWithState);
    onToggle(strollerId, location: HistoryLocationWithState);
}

type RouteProps = PageRouteProps<{}>;

export type Props = OwnProps & StateProps & DispatchProps & RouteProps;

export default class StrollersComparisonPage extends Page<
    OwnProps & RouteProps,
    StateProps,
    DispatchProps
> {
    public mapStateToProps(): MapStateToPropsParam<StateProps, OwnProps & RouteProps, AppState> {
        return (state: AppState, ownProps: OwnProps & RouteProps): StateProps => {
            const strollersComparisonState = getStrollersComparisonState(state);
            const strollersPreviewPhotoMap = strollersComparisonState.strollersPreviewPhotoMap;

            const strollersPhotos = getDenormalizedEntities<StrollerPhoto>(
                state,
                PhotoDetailSchema,
                strollersComparisonState.strollersPhotosIds,
            );
            const strollersPhotosMap = {};
            strollersPhotos.map(
                (strollerPhoto) => (strollersPhotosMap[strollerPhoto.id] = strollerPhoto),
            );

            return {
                loadingState: strollersComparisonState.loadingState,
                errorMessage: strollersComparisonState.errorMessage,
                requestUser: getRequestUser(state),
                strollers: getDenormalizedEntities<StrollerEntity>(
                    state,
                    StrollerSchema,
                    strollersComparisonState.strollersIds,
                ),
                strollersPreviewPhotoMap,
                strollersPhotosMap,
                count: strollersComparisonState.count,
                comparison: strollersComparisonState.allIds,
            };
        };
    }

    public mapDispatchToProps(): MapDispatchToPropsObject<DispatchProps> {
        return {
            onLoad: strollersComparisonLoad,
            onRemoveAll: strollersComparisonRemoveAll,
            onStoreOrdering: strollersComparisonStoreOrdering,
            onToggle: strollersComparisonToggle,
        };
    }

    public getMetaTitle(props: Props): string {
        return STROLLERS_COMPARISON_TITLE;
    }

    public getMetaDescription(props: Props): string {
        return `${STROLLERS_COMPARISON_TITLE} - ${STROLLER_SEO_GENERAL_DESC}`;
    }

    public render(props: Props): false | JSX.Element {
        return <StrollersComparison {...props} />;
    }

    public hasHeaderBackButton(props: Props): boolean {
        return true;
    }

    public getDefaultBackPath(props: Props): string {
        return strollersUrl;
    }

    public getHalfpageProps(props: Props): HalfpageProps {
        if (props.strollers?.length) {
            return {
                zone: "Strollers - Top 02 - Desktop",
            };
        }
        return null;
    }

    public getHeaderTitle(props: Props): string {
        return STROLLERS_COMPARISON;
    }

    public getHeaderRight(props: Props): React.ReactElement {
        return (
            isAuthenticated(props.requestUser) &&
            Boolean(props.strollers?.length) && (
                <Btn
                    className={styles.Brands__leftButton2}
                    type={BtnType.RedText}
                    label={STROLLERS_COMPARISON_REMOVE_ALL}
                    icon={faBalanceScaleLeft}
                    onClick={props.onRemoveAll}
                />
            )
        );
    }

    public registerInStore(store: Store<AppState>) {
        store.injectReducer("containers.strollers.strollersComparison", reducer);
        store.injectSaga("containers.strollers.strollersComparison", saga);
    }
}
